import { Fragment, useState } from "react";
import type { CvrBaseline, FunnelStep, ResponseBreakdown } from "../api";
import { ResponseBreakdownRow } from "./ResponseBreakdownRow";
import { RandomizedChip } from "./RandomizedChip";

type Props = {
  started: number;
  completed: number;
  steps: FunnelStep[];
  questionLabels: Record<string, string>;
  optionLabels: Record<string, Record<string, string>>;
  questionTypes: Record<string, string>;
  denominator: "answered" | "started";
  // Per-question responses, fetched lazily and keyed by question_id. A key is
  // absent until that question's breakdown request resolves (then it's an array,
  // possibly empty). Steps' own `responses` are no longer populated inline.
  breakdowns: Record<string, ResponseBreakdown[]>;
  breakdownsLoading?: boolean;
  // Set when breakdowns were deliberately NOT fetched -- today, because an arm
  // filter is active (rule 25a). The expanded row states this instead of
  // rendering an empty response grid, which would otherwise read as "nobody
  // answered this screen". Optional and null by default, so every existing
  // caller renders identically.
  breakdownsUnavailableReason?: string | null;
  // Question ids whose options are randomized in the widget; renders a read-only
  // badge. Derived from the quiz registry by Analytics, absent for other callers.
  randomizedQuestionIds?: Set<string>;
  // The quiz's own CVR over the same window/cohort/persona, forwarded to every
  // expanded ResponseBreakdownRow so its per-answer delta has something to
  // compare against.
  cvrBaseline: CvrBaseline;
  // The server's suppression floor, echoed through so the row's label states
  // the real number. `null` when the response did not carry one -- callers must
  // NOT substitute a literal, because a hardcoded 30 would render "n<30" on
  // cells the server suppressed at a different threshold.
  cvrMinOrders: number | null;
  // Endgame counts. All optional with a 0 default so every existing caller
  // renders exactly as it does today.
  offerViewed?: number;
  emailGateViewed?: number;
  emailGateSubmitted?: number;
  // Which tail rows this quiz actually has, derived from the quiz DEFINITION
  // by Analytics -- never from whether the count is non-zero, which would hide
  // the rows on precisely the gate that is converting at zero.
  showOfferRow?: boolean;
  showGateRows?: boolean;
  // False for calculator quizzes. `completed` fires on entering the reducer's
  // result phase, which a calculator never reaches (it drives its own state
  // machine, rule 26), so the row has only ever displayed a structural 0 --
  // and leaving it between Quiz start and the gate rows would make every
  // delta computed across it meaningless.
  showCompletionRow?: boolean;
};

function fmtDuration(ms: number | null): string {
  if (ms === null) return "—";
  const seconds = Math.round(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `0:${String(s).padStart(2, "0")} min`;
  return `${m}:${String(s).padStart(2, "0")} min`;
}

function fmtPct(value: number | null): string {
  if (value === null) return "—";
  return `${value.toFixed(2)}%`;
}

export function PagesTable(props: Props) {
  const {
    started,
    completed,
    steps,
    questionLabels,
    optionLabels,
    questionTypes,
    denominator,
    breakdowns,
    breakdownsLoading,
    breakdownsUnavailableReason = null,
    randomizedQuestionIds,
    cvrBaseline,
    cvrMinOrders,
    offerViewed = 0,
    emailGateViewed = 0,
    emailGateSubmitted = 0,
    showOfferRow = false,
    showGateRows = false,
    showCompletionRow = true,
  } = props;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const startExits = Math.max(0, started - (steps[0]?.viewed ?? started));
  const startDropOff =
    started > 0 ? Number(((startExits / started) * 100).toFixed(2)) : null;

  // The tail, as a list, so exits are derived between CONSECUTIVE RENDERED
  // rows. Computing them against a fixed sequence would produce a delta across
  // a row this quiz does not have -- which is exactly the defect that removing
  // the calculator's zero Completion row would otherwise create.
  const tail: { label: string; views: number; note?: string }[] = [
    ...(showCompletionRow ? [{ label: "Completion", views: completed }] : []),
    ...(showOfferRow
      ? [
          {
            label: "Offer",
            views: offerViewed,
            // Deliberately NOT a global date. `offer_viewed` began
            // 2026-08-19, but the Boost quiz renamed ignite -> quiz-v6 on
            // 2026-09-17 with no data migration, so quiz-v6's own rollup
            // rows only exist from the rename onward -- a bare "2026-08-19"
            // would be false for the biggest quiz on this dashboard, twice
            // over (see docs/runbooks/2026-09-17-endgame-rollup-backfill.md
            // section 7 for the full reasoning, including the separate
            // "not backfilled yet" zero this note also covers). State only
            // what this quiz's own rollup can support.
            note: "Days before this quiz's rollup was backfilled read 0 here.",
          },
        ]
      : []),
    ...(showGateRows
      ? [
          { label: "Email gate shown", views: emailGateViewed },
          { label: "Email given", views: emailGateSubmitted },
        ]
      : []),
  ];

  return (
    <div className="rounded-2xl border border-mars-line bg-mars-charcoal/40 p-6">
      <div className="eyebrow mb-4">Pages</div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr className="border-b border-mars-line text-left">
              <Th>#</Th>
              <Th>Pages</Th>
              <Th align="right">Page views</Th>
              <Th align="right">Exits</Th>
              <Th align="right">Drop-off rate</Th>
              <Th align="right">Time on Page</Th>
            </tr>
          </thead>
          <tbody>
            <BoundaryRow
              idx={1}
              label="Quiz start"
              views={started}
              exits={startExits}
              dropOff={startDropOff}
            />
            {steps.map((s, i) => {
              const label = s.question_id
                ? questionLabels[s.question_id] ?? s.label
                : s.label;
              const isOpen = expanded.has(s.key);
              const canExpand = s.breakdown_kind !== "skip";
              const labelMap = optionLabels[s.question_id ?? ""] ?? {};
              const qid = s.question_id ?? "";
              const isRandomized = randomizedQuestionIds?.has(qid) ?? false;
              const responses = breakdowns[qid] ?? [];
              const breakdownLoaded = qid in breakdowns;
              const leader =
                s.breakdown_kind === "options"
                  ? responses.find((r) => labelMap[r.answer_id] !== undefined)
                  : undefined;
              const leaderPct = leader
                ? denominator === "answered"
                  ? leader.pct_of_answered
                  : leader.pct_of_started
                : null;
              const toggle = () =>
                setExpanded((cur) => {
                  const next = new Set(cur);
                  if (next.has(s.key)) next.delete(s.key);
                  else next.add(s.key);
                  return next;
                });
              return (
                <Fragment key={s.key}>
                  <tr
                    className={
                      isOpen
                        ? "border-b border-mars-line/60 bg-mars-orange/[0.04]"
                        : "border-b border-mars-line/60 transition-colors hover:bg-mars-white/[0.02]"
                    }
                  >
                    <Td muted>{i + 2}</Td>
                    <Td>
                      {canExpand ? (
                        <button
                          type="button"
                          onClick={toggle}
                          className="flex w-full flex-col items-start gap-1 text-left text-mars-white"
                        >
                          <span className="flex items-center gap-2">
                            <span
                              aria-hidden
                              className={
                                isOpen
                                  ? "text-mars-orange"
                                  : "text-mars-white/50"
                              }
                            >
                              {isOpen ? "▾" : "▸"}
                            </span>
                            <span className="hover:text-mars-orange">
                              {label}
                            </span>
                            {isRandomized ? <RandomizedChip /> : null}
                          </span>
                          {!isOpen && leader && leaderPct !== null && (
                            <span className="ml-5 flex items-center gap-2 text-[0.7rem] text-mars-white/60">
                              <span className="mono uppercase tracking-[0.14em] text-mars-white/40">
                                Top
                              </span>
                              <span className="text-mars-white/80">
                                {labelMap[leader.answer_id]}
                              </span>
                              <span className="mono tabular-nums text-mars-orange">
                                {leaderPct.toFixed(2)}%
                              </span>
                            </span>
                          )}
                        </button>
                      ) : (
                        <span>{label}</span>
                      )}
                    </Td>
                    <Td align="right" mono>
                      {s.viewed.toLocaleString("en-US")}
                    </Td>
                    <Td align="right" mono>
                      {s.exits.toLocaleString("en-US")}
                    </Td>
                    <Td
                      align="right"
                      mono
                      muted={s.drop_off_pct === null}
                    >
                      {fmtPct(s.drop_off_pct)}
                    </Td>
                    <Td
                      align="right"
                      mono
                      muted={s.time_on_page_ms === null}
                    >
                      {fmtDuration(s.time_on_page_ms)}
                    </Td>
                  </tr>
                  {isOpen && canExpand && (
                    <tr className="bg-mars-night/40">
                      <td colSpan={6}>
                        {breakdownsUnavailableReason !== null ? (
                          <div
                            className="px-6 py-5 text-sm text-mars-white/50"
                            data-testid="breakdown-unavailable"
                          >
                            {breakdownsUnavailableReason}
                          </div>
                        ) : s.breakdown_kind === "options" &&
                          !breakdownLoaded &&
                          breakdownsLoading ? (
                          <div className="px-6 py-5 text-sm text-mars-white/50">
                            Loading response breakdown…
                          </div>
                        ) : (
                          <ResponseBreakdownRow
                            responses={responses}
                            optionLabelMap={
                              optionLabels[s.question_id ?? ""] ?? {}
                            }
                            denominator={denominator}
                            isMulti={
                              (s.question_id &&
                                questionTypes[s.question_id]) === "multi"
                            }
                            kind={
                              s.breakdown_kind === "free_text"
                                ? "free_text"
                                : "options"
                            }
                            baseline={cvrBaseline}
                            cvrMinOrders={cvrMinOrders}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {tail.map((row, i) => {
              const next = tail[i + 1];
              // The last rendered row is terminal: there is no next screen to
              // measure a loss against, so it shows no exits -- the same thing
              // the Completion row has always done.
              const exits =
                next === undefined ? null : Math.max(0, row.views - next.views);
              const dropOff =
                exits === null || row.views === 0
                  ? null
                  : Number(((exits / row.views) * 100).toFixed(2));
              return (
                <BoundaryRow
                  key={row.label}
                  idx={steps.length + 2 + i}
                  label={row.label}
                  views={row.views}
                  exits={exits}
                  dropOff={dropOff}
                  note={row.note}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BoundaryRow({
  idx,
  label,
  views,
  exits,
  dropOff,
  note,
}: {
  idx: number;
  label: string;
  views: number;
  exits?: number | null;
  dropOff?: number | null;
  note?: string | undefined;
}) {
  return (
    <tr className="border-b border-mars-line/60">
      <Td muted>{idx}</Td>
      <Td>
        {label}
        {note && (
          <span className="ml-2 font-normal text-mars-white/40" title={note}>
            ⓘ
          </span>
        )}
      </Td>
      <Td align="right" mono>
        {views.toLocaleString("en-US")}
      </Td>
      <Td align="right" mono muted={exits === null || exits === undefined}>
        {exits === null || exits === undefined
          ? "—"
          : exits.toLocaleString("en-US")}
      </Td>
      <Td
        align="right"
        mono
        muted={dropOff === null || dropOff === undefined}
      >
        {dropOff === null || dropOff === undefined ? "—" : fmtPct(dropOff)}
      </Td>
      <Td align="right" mono muted>
        —
      </Td>
    </tr>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={
        align === "right"
          ? "mono pb-3 text-right text-[0.6rem] font-medium uppercase tracking-[0.16em] text-mars-white/55"
          : "mono pb-3 text-left text-[0.6rem] font-medium uppercase tracking-[0.16em] text-mars-white/55"
      }
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono,
  muted,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
  muted?: boolean;
}) {
  const colorClass = muted ? "text-mars-white/40" : "text-mars-white";
  const fontClass = mono ? "mono tabular-nums" : "";
  const alignClass = align === "right" ? "text-right" : "text-left";
  return (
    <td className={`py-3 text-sm ${alignClass} ${fontClass} ${colorClass}`}>
      {children}
    </td>
  );
}
